// @ts-nocheck
import { useCallback } from 'react';
import type { Analysis } from '../../../services/AnalysisApiClient';
import { patchClientWorkspaceCompany, updateAnalysisData } from '../../../services/AnalysisApiClient';
import { isServerBackedAnalysisId } from '../../../utils/isServerBackedAnalysisId';
import { useCompanyContext } from '../context/CompanyContext';
import { useClientWorkspaceCompanyCard } from './useClientWorkspaceCompanyCard';

/** Persist closing date from Transaction Details (company row or active analysis basics). */
export function useSaveClientClosingDate(archivedAnalyses?: Analysis[]) {
  const { data, setData, currentAnalysisId, refreshAnalysesList } = useCompanyContext();
  const { workspaceCompany, refreshWorkspaceCompany } = useClientWorkspaceCompanyCard(archivedAnalyses);

  const linkedCompanyId = (workspaceCompany?.companyId ?? '').trim();
  const activeAnalysisId = String(currentAnalysisId ?? '').trim();
  const canEditClosing = Boolean(linkedCompanyId || isServerBackedAnalysisId(activeAnalysisId));

  const saveClosingDate = useCallback(
    async (isoYmd: string) => {
      const next = isoYmd.trim();
      if (!next) throw new Error('Closing date is required.');

      if (linkedCompanyId) {
        await patchClientWorkspaceCompany({ closingDate: next });
        await refreshWorkspaceCompany();
        await refreshAnalysesList();
        setData((prev) => ({ ...prev, closingDate: next }));
        return;
      }

      if (!isServerBackedAnalysisId(activeAnalysisId)) {
        throw new Error('Link a company or open a saved analysis to set the closing date.');
      }

      await updateAnalysisData(activeAnalysisId, {
        companyName: String(data?.companyName ?? ''),
        closingDate: next,
        dealPrice: data?.dealPrice,
        afr: data?.afr,
        headcount: data?.headcount,
      });
      await refreshAnalysesList();
      setData((prev) => ({ ...prev, closingDate: next }));
    },
    [linkedCompanyId, activeAnalysisId, refreshWorkspaceCompany, refreshAnalysesList, setData, data]
  );

  return { saveClosingDate, canEditClosing };
}
